import { Button } from '@design-system';
import { LicensedDone } from './licensed-done';
import { FreemiumPaywallFlow } from './freemium-paywall-flow';
import { WaterlilyLogo } from '@/components/WaterlilyLogo';

import { X } from 'lucide-react';

import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { selectClient } from '@/model';

interface FreemiumPlanningGateProps {
  clientId: string;
  clientFirstName: string;
  handoffPending: boolean;
  isFinalizing?: boolean;
  onBackToPreviousSlide: () => void;
}

export function FreemiumPlanningGate({
  clientId,
  clientFirstName,
  handoffPending,
  isFinalizing = false,
  onBackToPreviousSlide,
}: FreemiumPlanningGateProps) {
  const navigate = useNavigate();
  const client = useSelector(selectClient);

  function handleClose() {
    if (client?.advisorId) {
      navigate(`/advisors/${client.advisorId}/clients`);
    } else {
      navigate(-1);
    }
  }

  return (
    <div className="bg-background fixed inset-0 z-50 flex flex-col">
      <header className="flex shrink-0 items-center justify-between border-b p-4 md:px-6">
        <WaterlilyLogo className="h-7 w-auto" />
        <Button
          type="button"
          aria-label="Close"
          onClick={handleClose}
          variant="outline"
          size="icon-sm"
          className="rounded-full"
        >
          <X />
        </Button>
      </header>

      <div className="flex-1 overflow-y-auto">
        <div className="flex min-h-[calc(100vh-70px)] items-center justify-center px-4 py-10 md:px-6">
          {isFinalizing ? (
            <div className="flex flex-col items-center gap-4 py-16 text-center">
              <div className="border-primary size-10 animate-spin rounded-full border-4 border-t-transparent" />
              <p className="text-foreground font-medium">Finalizing your purchase…</p>
              <p className="text-muted-foreground text-sm">This only takes a moment.</p>
            </div>
          ) : handoffPending ? (
            <LicensedDone
              clientId={clientId}
              clientFirstName={clientFirstName}
              onClose={handleClose}
            />
          ) : (
            <FreemiumPaywallFlow
              clientId={clientId}
              clientFirstName={clientFirstName}
              onExit={onBackToPreviousSlide}
            />
          )}
        </div>
      </div>
    </div>
  );
}
